import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Checkbox } from 'tea-component';
import moment from 'moment';
import { getLanguage } from '@home/utils/common';
import HighLightText from '@home/components/HighLightText';
import { LANGUAGE_EN } from '@home/_config/config';
import { getLocale, useIntl } from 'umi';
import type { IntelligenceReportDataInfo } from '../../type';
import { splitReportTags } from '../../util';
import './index.scss';
import GameImage from '@home/components/GameImage';
import { formatReportLike } from '@intelligence/pages/InsightsReport/_config';
import LikeBtn from '@intelligence/assets/svg/tipicon/like.svg';

export interface IReportItemProps {
  data: IntelligenceReportDataInfo;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean, data: IntelligenceReportDataInfo) => void;
  searchKeyword?: string;
  style?: React.CSSProperties;
  /** 标签分隔符正则（字符串形式），默认值 [,|/\\、；;。.] */
  tagSeparatorReg?: string;
}

const ReportItem: React.FC<IReportItemProps> = (props) => {
  const { data, checked, disabled, onChange, searchKeyword = '', style, tagSeparatorReg } = props;
  const locale = getLocale();
  const isEn = locale === LANGUAGE_EN;
  const { formatMessage } = useIntl();

  const title = useMemo(() => {
    return getLanguage(isEn, data.title_en, data.title) || '-';
  }, [isEn, data.title_en, data.title]);

  const reportType = useMemo(() => {
    return getLanguage(isEn, data.report_type_en, data.report_type) || '';
  }, [isEn, data.report_type_en, data.report_type]);

  const tags = useMemo(() => {
    const tagList = isEn ? data.tag_en : data.tag;
    return splitReportTags(tagList, tagSeparatorReg);
  }, [isEn, data.tag_en, data.tag, tagSeparatorReg]);

  const createTime = useMemo(() => {
    return data.create_time ? moment(data.create_time).format('YYYY-MM-DD') : '';
  }, [data.create_time]);

  const handleChange = (value: boolean) => {
    onChange?.(value, data);
  };

  return (
    <div className={classNames('dbh-report-item', { disabled })} style={style}>
      <Checkbox
        value={checked}
        disabled={disabled}
        onChange={handleChange}
        className="dbh-report-item-checkbox"
      >
        <div className="dbh-report-item-content">
          <div className="dbh-report-item-cover">
            <GameImage

              src={data.cover === 'error' ? '' : data.cover}
              alt={'flag'}
              entityType={'report'}
            />
          </div>
          <div className="dbh-report-item-info">
            <div className="dbh-report-item-title">
              <HighLightText text={title} keyword={searchKeyword} />
            </div>
            <div className="dbh-report-item-meta">
              <span className="dbh-report-item-time">{createTime}</span>
           
              {reportType && (
                <>
                  <span className="dbh-report-item-divider">|</span>
                  <span className="dbh-report-item-type">{reportType}</span>
                </>
              )}
              {data?.report_like && (
                <>
                  <span className="dbh-report-item-divider">|</span>
                  <img className="dbh-report-item-like-img" src={LikeBtn} alt="" />
                  {`${formatMessage({ id: 'dbi_reports_liked' })} ${formatReportLike(
                    data?.report_like,
                  )}`}
                </>
              ) || null}
            </div>
            {tags.length > 0 && (
              <div className="dbh-report-item-tags">
                {tags.map((tag, index) => (
                  <span key={index} className="dbh-report-item-tag">
                    <HighLightText text={tag} keyword={searchKeyword} />
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </Checkbox>
    </div>
  );
};

export default ReportItem;
